import { _, helpers, React, ts, ui, useSelector } from '_core';

import { Params } from './types';

type ChartProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  file: string;
  fullScreen: boolean;
  params: Params;
  context: ts.types.timeSeries.TimeSeriesAnalysisContext;
  widgetTitle: string;
  setExternalEmbed: (_json: ts.types.analysis.EmbedChartData) => void;
};

const Chart = ({
  id,
  readOnly,
  loadWidgetData,
  file,
  params,
  context,
  fullScreen,
  widgetTitle,
  setExternalEmbed,
}: ChartProps) => {
  const [data, setData] = React.useState<ts.types.widgets.ScatterData>(null);
  const [error, setError] = React.useState<ts.types.common.ApiError>(null);

  const timeSeries = useSelector((state) => state.resources.time_series);

  const ts1 = timeSeries.find((t) => t.id == (context.time_series_id ?? params.time_series_id));
  const ts2 = timeSeries.find((t) => t.id == params.compare_time_series_id);

  const loadData = async () => {
    try {
      const response = await loadWidgetData({
        data: {
          file,
          rename: { build_date: 'date' },
        },
      });
      if (response) setData(response.data as ts.types.widgets.ScatterData);
    } catch (err) {
      setError(err as ts.types.common.ApiError);
    }
  };

  React.useEffect(() => {
    if (file) loadData();
    return () => setData(null);
  }, [file]);

  const title = React.useMemo(() => {
    if (!data) return null;
    const corr = data?.[0]?.['corr'] || null;
    const m = data?.[0]?.['m'] || null;
    const b = data?.[0]?.['b'] || null;
    const eq = helpers.buildName.getEquationStr(m, b);

    const corrLabel = `[bold]Correlation:[/] ${corr ? _.round(corr, 4) : 'Unable to process it.'}`;
    const equationLabel = `[bold]${!_.isEmpty(eq) ? 'y = ' + eq : 'Unable to process the equation.'}[/]`;

    return corrLabel + '\u00A0\u00A0\u00A0\u00A0' + equationLabel;
  }, [data]);

  return (
    <ui.ScatterPlotChart
      id={id}
      fullScreen={fullScreen}
      readOnly={readOnly}
      data={data}
      alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
      xAxisField={ts2.handle}
      yAxisField={ts1.handle}
      xAxisLabel={ts2.name}
      lineOverlay={data?.[0]?.fit ? 'fit' : undefined}
      yAxisLabel={ts1.name}
      exportTitle={widgetTitle}
      numberFormat="#.##"
      setExternalEmbed={setExternalEmbed}
      title={title}
      hideLegend
    />
  );
};

export default Chart;
